import React, { useState, useEffect } from 'react';
import { Subheader, Toggle, SearchSelect } from './FieldComponents';
import { CheckCircle, RefreshCw, XCircle } from 'lucide-react';

export const QuickbooksIntegration = ({ user, connected, setConnected }) => {
  const [loading, setLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState({
    lastSync: null,
    syncInProgress: false,
    error: null
  });
  const [settings, setSettings] = useState({
    autoSync: false,
    syncFrequency: 'daily',
    defaultExpenseAccount: '',
    defaultIncomeAccount: '',
    defaultClass: '',
    createVendors: true,
    createCustomers: true
  });
  const [accountMappings, setAccountMappings] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [dataChanged, setDataChanged] = useState(false);

  useEffect(() => {
    // Fetch integration status and settings
    const fetchIntegrationData = async () => {
      setLoading(true);
      try {
        // Get QB connection status
        const statusResponse = await fetch('https://api.casedb.co/accounting.php?action=qb_status', { credentials: 'include' });
        const statusData = await statusResponse.json();
        setConnected(statusData.connected || false);
        
        if (statusData.connected) {
          // Get sync status
          const syncResponse = await fetch('https://api.casedb.co/accounting.php?action=sync_status', { credentials: 'include' });
          const syncData = await syncResponse.json();
          
          if (syncData.success) {
            setSyncStatus({
              lastSync: syncData.lastSync,
              syncInProgress: syncData.syncInProgress || false,
              error: syncData.error || null
            });
          }
          
          // Get QB settings
          const settingsResponse = await fetch('https://api.casedb.co/accounting.php?action=qb_settings', { credentials: 'include' });
          const settingsData = await settingsResponse.json();
          
          if (settingsData.success) {
            setSettings(settingsData.settings);
          }
          
          // Get account options
          const accountsResponse = await fetch('https://api.casedb.co/accounting.php?action=accounts', { credentials: 'include' });
          const accountsData = await accountsResponse.json();
          
          if (accountsData.success) {
            setAccountOptions(accountsData?.accounts || []);
          }
          
          // Get class options
          const classesResponse = await fetch('https://api.casedb.co/accounting.php?action=classes', { credentials: 'include' });
          const classesData = await classesResponse.json();
          
          if (classesData.success) {
            setClassOptions(classesData.classes || []);
          }
          
          // Get account mappings
          const mappingsResponse = await fetch('https://api.casedb.co/accounting.php?action=account_mappings', { credentials: 'include' });
          const mappingsData = await mappingsResponse.json();
          
          if (mappingsData.success) {
            setAccountMappings(mappingsData.mappings || []);
          }
        }
      } catch (error) {
        console.error('Error fetching QuickBooks integration data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchIntegrationData();
  }, [setConnected]);
  
  const handleConnect = () => {
    window.location.href = 'https://api.casedb.co/quickbooks_connect.php';
  };
  
  const handleDisconnect = async () => {
    if (window.confirm('Are you sure you want to disconnect from QuickBooks? This will stop all syncing capabilities.')) {
      setLoading(true);
      try {
        const response = await fetch('https://api.casedb.co/accounting.php?action=qb_disconnect', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            user_id: user?.user_id
          }),
        });
        
        const data = await response.json();
        if (data.success) {
          setConnected(false);
        } else {
          console.error('Error disconnecting from QuickBooks:', data.message);
        }
      } catch (error) {
        console.error('Error disconnecting from QuickBooks:', error);
      } finally {
        setLoading(false);
      }
    }
  };
  
  const handleSync = async () => {
    setLoading(true);
    setSyncStatus(prev => ({ ...prev, syncInProgress: true, error: null }));
    
    try {
      const response = await fetch('https://api.casedb.co/accounting.php?action=sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          user_id: user?.user_id
        }),
      });
      
      const data = await response.json();
      if (data.success) {
        setSyncStatus({
          lastSync: new Date().toISOString(),
          syncInProgress: false,
          error: null
        });
      } else {
        setSyncStatus(prev => ({
          ...prev,
          syncInProgress: false,
          error: data.message || 'An error occurred during sync'
        }));
        console.error('Error syncing with QuickBooks:', data.message);
      }
    } catch (error) {
      setSyncStatus(prev => ({
        ...prev,
        syncInProgress: false,
        error: 'Network error occurred during sync'
      }));
      console.error('Error syncing with QuickBooks:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setDataChanged(true);
  };
  
  const saveSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://api.casedb.co/accounting.php?action=save_qb_settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          user_id: user?.user_id,
          settings
        }),
      });
      
      const data = await response.json();
      if (data.success) {
        setDataChanged(false);
        alert('Settings saved successfully');
      } else {
        console.error('Error saving QuickBooks settings:', data.message);
        alert(`Error: ${data.message || 'Failed to save settings'}`);
      }
    } catch (error) {
      console.error('Error saving QuickBooks settings:', error);
      alert('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className="accounting-section">
      <Subheader title="QuickBooks Integration" />
      
      {loading && !connected ? (
        <div className="loading-container">Loading integration status...</div>
      ) : !connected ? (
        <div className="qb-connect-container">
          <div className="qb-status not-connected">
            <XCircle size={24} />
            <span>Not connected to QuickBooks</span>
          </div>
          
          <div className='t-center'>Connect your QuickBooks Online account to sync invoices, expenses, and other financial data automatically.</div>
          
          <button className="action" onClick={handleConnect} disabled={loading}>
            {loading ? 'Connecting...' : 'Connect to QuickBooks'}
          </button>
        </div>
      ) : (
        <div className="qb-container">
          <div className="qb-status-bar">
            <div className="qb-status connected">
              <CheckCircle size={20} />
              <span>Connected to QuickBooks Online</span>
            </div>
            
            <div className="qb-sync-status">
              <div className="sync-info">
                <span>Last sync: {formatDate(syncStatus.lastSync)}</span>
                {syncStatus.error && (
                  <span className="sync-error">Error: {syncStatus.error}</span>
                )}
              </div>
              
              <button 
                className="action alt" 
                onClick={handleSync} 
                disabled={loading || syncStatus.syncInProgress}
              >
                <RefreshCw size={16} className={syncStatus.syncInProgress ? 'spin' : ''} />
                {syncStatus.syncInProgress ? 'Syncing...' : 'Sync Now'}
              </button>
              
              <button className="action destroy" onClick={handleDisconnect} disabled={loading}>
                Disconnect
              </button>
            </div>
          </div>
          
          <div className="qb-settings">
            <Subheader title="Integration Settings" small={true} />
            
            <div className="settings-grid">
              <div className="settings-column">
                <div className="settings-group">
                  <h4>Sync Settings</h4>
                  
                  <div className="setting-item">
                    <Toggle
                      value={settings.autoSync}
                      onChange={(val) => handleSettingChange('autoSync', val)}
                      label="Enable automatic sync"
                    />
                  </div>
                  
                  {settings.autoSync && (
                    <div className="setting-item">
                      <label>Sync Frequency</label>
                      <select 
                        className="default-select"
                        value={settings.syncFrequency}
                        onChange={(e) => handleSettingChange('syncFrequency', e.target.value)}
                      >
                        <option value="hourly">Hourly</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                      </select>
                    </div>
                  )}
                  
                  <div className="setting-item">
                    <Toggle
                      value={settings.createVendors}
                      onChange={(val) => handleSettingChange('createVendors', val)}
                      label="Automatically create vendors in QuickBooks"
                    />
                  </div>
                  
                  <div className="setting-item">
                    <Toggle
                      value={settings.createCustomers}
                      onChange={(val) => handleSettingChange('createCustomers', val)}
                      label="Automatically create customers in QuickBooks"
                    />
                  </div>
                </div>
              </div>
              
              <div className="settings-column">
                <div className="settings-group">
                  <h4>Default Accounts</h4>
                  
                  <div className="setting-item">
                    <label>Default Expense Account</label>
                    <SearchSelect
                      value={settings.defaultExpenseAccount}
                      onChange={(val) => handleSettingChange('defaultExpenseAccount', val)}
                      options={accountOptions?.filter(acc => acc.type === 'Expense')}
                      placeholder="Select default expense account"
                    />
                  </div>
                  
                  <div className="setting-item">
                    <label>Default Income Account</label>
                    <SearchSelect
                      value={settings.defaultIncomeAccount}
                      onChange={(val) => handleSettingChange('defaultIncomeAccount', val)}
                      options={accountOptions.filter(acc => acc.type === 'Income')}
                      placeholder="Select default income account"
                    />
                  </div>
                  
                  <div className="setting-item">
                    <label>Default Class (Optional)</label>
                    <SearchSelect
                      value={settings.defaultClass}
                      onChange={(val) => handleSettingChange('defaultClass', val)}
                      options={classOptions}
                      placeholder="Select default class"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="settings-actions">
              <button 
                className="action" 
                onClick={saveSettings} 
                disabled={!dataChanged || loading}
              >
                Save Settings
              </button>
            </div>
          </div>
          
          <div className="qb-mappings">
            <Subheader title="Account Mappings" small={true} />
            <div className='t-center'>These mappings determine which QuickBooks accounts are used for different transaction types.</div>
            
            <table className="mappings-table">
              <thead>
                <tr>
                  <th>Transaction Type</th>
                  <th>QuickBooks Account</th>
                </tr>
              </thead>
              <tbody>
                {accountMappings.map((mapping, index) => (
                  <tr key={index}>
                    <td>{mapping.type}</td>
                    <td>
                      <SearchSelect
                        value={mapping.account_id}
                        onChange={(val) => {
                          const newMappings = [...accountMappings];
                          newMappings[index].account_id = val;
                          setAccountMappings(newMappings);
                          setDataChanged(true);
                        }}
                        options={accountOptions}
                        placeholder="Select account"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            <div className="mappings-actions">
              <button 
                className="action" 
                onClick={saveSettings}
                disabled={!dataChanged || loading}
              >
                Save Mappings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickbooksIntegration;
